/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Tab, Tabs, Box, AppBar, TextField, IconButton, Menu, MenuItem, alpha } from '@mui/material';
import { useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import { Sort } from '@mui/icons-material';
import Page from '../../../../components/Page';
import Iconify from '../../../../components/Iconify';
import { dispatch } from '../../../../redux/store';
import { getUserPortfolio } from '../../../../redux/slices/trades';
import useTrade from '../../../../hooks/useTrade';
import MemoizedPortfolioTradesList from './MemoizedPortfolioTradesList';
import MemoizedPortfolioHeader from './MemoizedPortfolioHeader';

const SORT_STORAGE_KEY = 'portfolioSort';

// Reusable TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{
        height: value === index ? '100%' : 0,
        overflow: value === index ? 'auto' : 'hidden',
        visibility: value === index ? 'visible' : 'hidden',
        display: value === index ? 'block' : 'none'
      }}
    >
      <Box sx={{ pb: 3 }}>{children}</Box>
    </div>
  );
}

function Portfolio() {
  // Only select what we need at the top level
  const userTrades = useSelector((state) => state?.trade?.userTrades);
  const priceChangeConfig = useSelector((state) => state?.trade?.priceChangeConfig);
  
  const { localSocket } = useTrade();
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortType, setSortType] = useState(() => {
    const savedSort = localStorage.getItem(SORT_STORAGE_KEY);
    return savedSort || 'default';
  });
  // We no longer need these functions since filtering and sorting
  // will be handled by the memoized components

  // Sorting handlers
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (type) => {
    setSortType(type);
    handleSortClose();
    localStorage.setItem(SORT_STORAGE_KEY, type);
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(getUserPortfolio());
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };

    fetchData();
  }, []);

  // WebSocket subscription logic
  useEffect(() => {
    const tokenArray =
      userTrades && Object.keys(userTrades).length > 0
        ? [
          ...userTrades?.OPEN?.map((trade) => trade?.script?.instrument_token),
          ...userTrades?.PENDING?.map((trade) => trade?.script?.instrument_token),
          ...userTrades?.ORDER?.map((trade) => trade?.script?.instrument_token),
        ]
        : [];
    if (tokenArray.length) {
      const payload = tokenArray.map((token) => +token);

      const sendSubscribeMessage = () => {
        console.log('Sending subscribe message:', payload);
        localSocket.send(JSON.stringify({ event: 'subscribe', data: payload }));
      };

      if (localSocket) {
        if (localSocket.readyState === WebSocket.OPEN) {
          sendSubscribeMessage();
        } else {
          localSocket.onopen = () => {
            console.log('WebSocket connection opened');
            sendSubscribeMessage();
          };
        }
      } else {
        console.log('localSocket is not defined');
      }
    }

    return () => {
      if (localSocket && localSocket.readyState === WebSocket.OPEN) {
        if (tokenArray.length > 0) {
          console.log('Sending unsubscribe message:', tokenArray);
          localSocket.send(JSON.stringify({ event: 'unsubscribe', data: tokenArray.map((token) => +token) }));
        }
      }
    };
  }, [userTrades, localSocket, localSocket?.readyState]);

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle swipe index change
  const handleSwipeChangeIndex = (index) => {
    setTabValue(index);
  };  return (
    <Page sx={{ px: 0, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <MemoizedPortfolioHeader />
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <AppBar position="static" color="default" sx={{ position: 'sticky', top: 0, zIndex: 1, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                sx={{ width: '100%', display: 'flex' }}
              >
                <Tab label="NET ACTIVE" value={0} sx={{ flexGrow: 1, maxWidth: 'none' }} />
                <Tab label="NET CLOSED" value={1} sx={{ flexGrow: 1, maxWidth: 'none' }} />
              </Tabs>

              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={handleSortClick}
                  sx={{
                    height: 32,
                    width: 32,
                    color: sortType !== 'default' ? 'primary.main' : 'text.disabled',
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                    '&:hover': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                >
                  <Sort />
                </IconButton>

                <TextField
                  fullWidth
                  placeholder="Search Symbols"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Iconify
                        icon="eva:search-fill"
                        sx={{
                          color: 'text.disabled',
                          width: 16,
                          height: 16,
                          mr: 0.5
                        }}
                      />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                      '& fieldset': {
                        borderColor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        borderWidth: 1,
                      },
                      '&:hover': {
                        backgroundColor: 'background.paper',
                        '& fieldset': {
                          borderColor: 'primary.lighter',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                        boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 1,
                        },
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&::placeholder': {
                        color: 'text.disabled',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem
                onClick={() => handleSortSelect('default')}
                sx={{
                  color: sortType === 'default' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'default' ? 600 : 400,
                }}
              >
                Default Order
              </MenuItem>
              <MenuItem
                onClick={() => handleSortSelect('atoz')}
                sx={{
                  color: sortType === 'atoz' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'atoz' ? 600 : 400,
                }}
              >
                Sort A to Z
              </MenuItem>
              <MenuItem
                onClick={() => handleSortSelect('lastUpdated')}
                sx={{
                  color: sortType === 'lastUpdated' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'lastUpdated' ? 600 : 400,
                }}
              >
                Sort by Last Updated
              </MenuItem>
            </Menu>
          </AppBar>
          <SwipeableViews
            index={tabValue}
            onChangeIndex={handleSwipeChangeIndex}
            style={{ flexGrow: 1, height: 'calc(100% - 48px)', overflow: 'hidden' }}
            containerStyle={{ height: '100%' }}
            axis="x"
            resistance
          >
            <TabPanel value={tabValue} index={0}>
              {/* Memoized active trades list */}
              <MemoizedPortfolioTradesList
                trades={userTrades?.OPEN || []}
                tabValue={0}
                searchText={searchText}
                sortType={sortType}
                priceChangeConfig={priceChangeConfig}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {/* Memoized closed orders list */}
              <MemoizedPortfolioTradesList
                trades={userTrades?.ORDER || []}
                tabValue={1}
                searchText={searchText}
                sortType={sortType}
                priceChangeConfig={priceChangeConfig}
              />
            </TabPanel>
          </SwipeableViews>
        </Box>
      </Box>
    </Page>
  );
}

export default Portfolio;
